import React from 'react';
import { Mail, Download, Copy, Clock, CheckCircle } from 'lucide-react';
import { books } from '../../data/books';
import { BASE_URL } from '../../data/constants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

export const PurchaseModal = ({
  modalState,
  updateModalState,
  closeModal,
  handleEmailChange,
  handleEmailSubmit,
  copyPixCode,
  validateEmail,
  sales,
}) => {
  const currentBook = modalState.selectedBook ? books[modalState.selectedBook] : null;

  if (!modalState.isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <div className="bg-white w-full sm:rounded-2xl sm:max-w-md flex flex-col max-h-[92vh] rounded-t-2xl shadow-2xl">

        {/* Barra superior: botão X sempre visível, nunca rola */}
        <div className="flex-shrink-0 flex justify-end px-4 pt-3 pb-0">
          <button
            onClick={closeModal}
            className="text-gray-400 hover:text-gray-700 text-2xl font-bold w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Fechar modal"
          >
            ×
          </button>
        </div>

        {/* Conteúdo rolável */}
        <div className="overflow-y-auto flex-1 px-5 sm:px-8 pb-6 pt-1">

        {/* Mostrar erros */}
        {modalState.error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" role="alert">
            {modalState.error}
          </div>
        )}

        {/* Step: Email */}
        {modalState.step === 'email' && (
          <div>
            <div className="text-center mb-6">
              <div className="text-6xl mb-4" role="img" aria-label={`Ícone do ${currentBook?.title}`}>{currentBook?.emoji}</div>
              <h3 id="modal-title" className="text-2xl font-bold mb-2">
                Fortalecer a {modalState.selectedBook?.toUpperCase()}
              </h3>
              <p className="text-gray-600">{currentBook?.title}</p>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="email-input">
                <Mail className="inline mr-2" size={16} aria-hidden="true" />
                Seu E-mail para receber o ebook
              </label>
              <input
                id="email-input"
                type="email"
                value={modalState.email}
                onChange={(e) => handleEmailChange(e.target.value)}
                className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 text-lg transition-colors ${
                  modalState.emailError 
                    ? 'border-red-500 focus:border-red-500' 
                    : 'border-gray-300 focus:border-transparent'
                }`}
                placeholder="seuemail@gmail.com"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !modalState.isLoading) {
                    e.preventDefault(); // Previne submit do form
                    handleEmailSubmit();
                  }
                }}
                disabled={modalState.isLoading}
                aria-describedby={modalState.emailError ? "email-error" : modalState.email && !modalState.emailError && validateEmail(modalState.email) === '' ? "email-success" : undefined}
              />
              {modalState.emailError && (
                <p id="email-error" className="mt-2 text-sm text-red-600 flex items-center" role="alert">
                  <span className="mr-2" aria-hidden="true">⚠️</span>
                  {modalState.emailError}
                </p>
              )}
              {modalState.email && !modalState.emailError && validateEmail(modalState.email) === '' && (
                <p id="email-success" className="mt-2 text-sm text-green-600 flex items-center">
                  <span className="mr-2" aria-hidden="true">✅</span>
                  E-mail válido
                </p>
              )}
            </div>
            
            {/* Termo de aceite */}
            <div className="mb-4">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={modalState.termsAccepted}
                  onChange={(e) => updateModalState({ termsAccepted: e.target.checked })}
                  className="mt-0.5 w-4 h-4 accent-blue-600 flex-shrink-0 cursor-pointer"
                />
                <span className="text-xs text-gray-600 leading-relaxed">
                  Li e aceito os{' '}
                  <a
                    href="/termos.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline font-medium hover:text-blue-800"
                  >
                    Termos de Compra e Política de Privacidade
                  </a>
                </span>
              </label>
            </div>

            <button
              onClick={handleEmailSubmit}
              disabled={!modalState.email || validateEmail(modalState.email) !== '' || !modalState.termsAccepted || modalState.isLoading}
              className="w-full py-3 px-6 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-lg"
            >
              {modalState.isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" aria-hidden="true"></div>
                  Processando...
                </div>
              ) : (
                'Continuar para Pagamento'
              )}
            </button>
          </div>
        )}

        {/* Step: PIX */}
        {modalState.step === 'pix' && (
          <div>
            <div className="text-center mb-4">
              <h3 className="text-xl font-bold mb-1">Pagamento via PIX</h3>
              <div className="mt-1 inline-flex items-center gap-2 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                <div className="w-2 h-2 bg-green-600 rounded-full animate-pulse" aria-hidden="true"></div>
                Aguardando pagamento...
              </div>
            </div>
            <div className="flex flex-col items-center space-y-3">
              {/* QR Code */}
              <div className="w-36 h-36 bg-gray-100 border rounded-lg flex items-center justify-center overflow-hidden">
                {modalState.qrCodeImage ? (
                  <img 
                    src={modalState.qrCodeImage} 
                    alt="QR Code para pagamento PIX" 
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" aria-hidden="true"></div>
                )}
              </div>
              {/* Valor e pedido */}
              <div className="text-lg font-bold text-gray-800">R$ {currentBook?.priceValue?.toFixed(2).replace('.', ',')}</div>
              <div className="text-xs text-gray-500">Pedido: {modalState.orderId}</div>
              {/* Código PIX */}
              {modalState.pixCode && (
                <div className="w-full">
                  <div className="bg-white border rounded p-2 text-xs font-mono break-all text-gray-800 mb-2 max-h-16 overflow-y-auto">
                    {modalState.pixCode}
                  </div>
                  <button
                    id="copy-pix-btn"
                    onClick={copyPixCode}
                    className="w-full flex items-center justify-center gap-2 py-2 px-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-xs"
                    aria-label="Copiar código PIX para área de transferência"
                  >
                    <Copy size={14} aria-hidden="true" />
                    Copiar Código PIX
                  </button>
                </div>
              )}
              {/* Status */}
              <div className="flex items-center justify-center text-blue-700 text-xs mt-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2" aria-hidden="true"></div>
                Verificando pagamento...
              </div>
            </div>
          </div>
        )}

        {/* Step: Processing */}
        {modalState.step === 'processing' && (
          <div>
            <div className="text-center mb-6">
              <div className="text-6xl mb-4" role="img" aria-label="Processando">⏳</div>
              <h3 className="text-2xl font-bold mb-2 text-blue-600">
                Pagamento Confirmado!
              </h3>
              <p className="text-gray-600">
                Preparando seu ebook...
              </p>
            </div>

            <div className="space-y-4">
              {/* Indicador de progresso */}
              <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                <div className="flex items-center justify-center mb-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" aria-hidden="true"></div>
                </div>
                <p className="text-center text-blue-800 font-semibold">
                  ✅ Pagamento aprovado pelo Mercado Pago
                </p>
                <p className="text-center text-blue-600 text-sm mt-2">
                  Finalizando seu pedido...
                </p>
                <div className="text-center text-blue-500 text-sm mt-3">
                  <Clock size={14} className="inline mr-1" aria-hidden="true" />
                  {modalState.processingTimer}s
                </div>
              </div>

              {/* Informações do pedido */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">📦 Detalhes do Pedido</h4>
                <dl className="text-sm text-gray-600 space-y-1">
                  <div><dt className="inline">E-mail:</dt> <dd className="inline">{modalState.email}</dd></div>
                  <div><dt className="inline">Produto:</dt> <dd className="inline">{currentBook?.title}</dd></div>
                  <div><dt className="inline">Valor:</dt> <dd className="inline">R$ {currentBook?.priceValue?.toFixed(2).replace('.', ',')}</dd></div>
                  <div><dt className="inline">Método:</dt> <dd className="inline">PIX</dd></div>
                  <div><dt className="inline">ID:</dt> <dd className="inline">{modalState.orderId}</dd></div>
                </dl>
              </div>
            </div>
          </div>
        )}

        {/* Step: Success */}
        {modalState.step === 'success' && (
          <div>
            <div className="text-center mb-4">
              <div className="text-5xl mb-2" role="img" aria-label="Sucesso">🎉</div>
              <h3 className={`text-xl font-bold mb-1 ${currentBook?.textColor}`}>
                Pagamento Aprovado!
              </h3>
              <p className="text-gray-600 text-sm">
                Você fortaleceu a {modalState.selectedBook?.toUpperCase()}! 💪
              </p>

              <div className={`mt-3 p-3 rounded-lg border ${currentBook?.successBg} ${currentBook?.successBorder}`}>
                <CheckCircle className="mx-auto mb-1 text-green-600" size={20} aria-hidden="true" />
                <p className={`text-xs font-semibold ${currentBook?.successText}`}>
                  📧 Ebook enviado para: {modalState.email}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {/* Botão de download protegido */}
              <a
                href={`${BACKEND_URL}/download/${modalState.downloadToken}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`w-full flex items-center justify-center gap-2 py-3 px-5 text-white rounded-lg transition-colors font-semibold text-base ${currentBook?.successColor} ${currentBook?.successHover}`}
                aria-label={`Baixar ebook ${currentBook?.title}`}
              >
                <Download size={18} aria-hidden="true" />
                📥 Baixar Ebook (PDF)
              </a>

              {/* Compartilhamento */}
              {(() => {
                const lado = modalState.selectedBook;
                const isEsq = lado === 'esquerda';
                const emoji = isEsq ? '✊' : '💪';
                const ladoUpper = lado?.toUpperCase();
                const esqScore = sales?.esquerda ?? 0;
                const dirScore = sales?.direita ?? 0;

                // URL da página de share no frontend — nginx proxia /compartilhar para o backend (OG tags SSR)
                const frontUrl = process.env.REACT_APP_FRONT_URL || window.location.origin;
                const sharePageUrl = `${frontUrl}/compartilhar?lado=${lado}`;
                const shareText = `Acabei de fortalecer a ${ladoUpper}! A batalha ta acirrada - escolhe o seu lado:`;

                return (
                  <div>
                    {/* Preview da capa */}
                    <div className={`flex items-center gap-3 p-3 rounded-xl mb-4 border-2 ${currentBook?.borderColor} ${currentBook?.bgLight}`}>
                      <div className={`w-12 h-16 ${currentBook?.color} rounded-lg flex-shrink-0 flex flex-col items-center justify-center text-white shadow-md`}>
                        <div className="text-xl leading-none">{currentBook?.emoji}</div>
                        <div className="text-[7px] font-bold text-center px-1 leading-tight mt-1">{currentBook?.title?.split(' ').slice(0, 2).join(' ')}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-gray-800 truncate">{currentBook?.title}</p>
                        <p className={`text-xs font-semibold truncate ${currentBook?.textColor}`}>{currentBook?.subtitle}</p>
                        <p className="text-xs text-gray-500 mt-1 italic leading-snug">"{shareText.substring(0, 72)}…"</p>
                      </div>
                    </div>

                    <p className="text-xs text-gray-500 mb-3 text-center font-medium tracking-wide uppercase">Compartilhe sua escolha</p>
                    <div className="flex justify-center gap-4" role="group" aria-label="Compartilhar nas redes sociais">

                      {/* WhatsApp */}
                      <a
                        href={`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + sharePageUrl)}`}
                        target="_blank" rel="noopener noreferrer"
                        className="flex flex-col items-center gap-1 group"
                        aria-label="Compartilhar no WhatsApp"
                      >
                        <div className="w-12 h-12 bg-[#25D366] rounded-2xl flex items-center justify-center shadow-md group-hover:shadow-lg group-hover:scale-110 transition-all">
                          <svg viewBox="0 0 24 24" fill="white" className="w-7 h-7">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                          </svg>
                        </div>
                        <span className="text-[10px] text-gray-500 font-medium">WhatsApp</span>
                      </a>

                      {/* Twitter / X */}
                      <a
                        href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(sharePageUrl)}`}
                        target="_blank" rel="noopener noreferrer"
                        className="flex flex-col items-center gap-1 group"
                        aria-label="Compartilhar no X (Twitter)"
                      >
                        <div className="w-12 h-12 bg-black rounded-2xl flex items-center justify-center shadow-md group-hover:shadow-lg group-hover:scale-110 transition-all">
                          <svg viewBox="0 0 24 24" fill="white" className="w-6 h-6">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.742l7.737-8.835L1.254 2.25H8.08l4.261 5.635 5.903-5.635zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                          </svg>
                        </div>
                        <span className="text-[10px] text-gray-500 font-medium">Twitter / X</span>
                      </a>

                      {/* Facebook */}
                      <a
                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(sharePageUrl)}&quote=${encodeURIComponent(shareText)}`}
                        target="_blank" rel="noopener noreferrer"
                        className="flex flex-col items-center gap-1 group"
                        aria-label="Compartilhar no Facebook"
                      >
                        <div className="w-12 h-12 bg-[#1877F2] rounded-2xl flex items-center justify-center shadow-md group-hover:shadow-lg group-hover:scale-110 transition-all">
                          <svg viewBox="0 0 24 24" fill="white" className="w-6 h-6">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                          </svg>
                        </div>
                        <span className="text-[10px] text-gray-500 font-medium">Facebook</span>
                      </a>
                    </div>
                  </div>
                );
              })()}

              {/* Fechar */}
              <div className="text-center pt-3 border-t">
                <p className="text-gray-500 text-xs mb-2">
                  Obrigado por fortalecer a {modalState.selectedBook?.toUpperCase()}! 🙏
                </p>
                <button
                  onClick={closeModal}
                  className="text-blue-600 hover:text-blue-800 font-semibold text-sm underline"
                >
                  Fechar e voltar ao site
                </button>
              </div>
            </div>
          </div>
        )}

        </div>{/* fim overflow-y-auto */}
      </div>
    </div>
  );
};